import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PromoButtons } from '../components/promos/PromoButtons';
import { useGuestTitle } from '../lib/guestRoute';
import { Loader2, ExternalLink, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CardScanActions } from '../components/shared/CardScanActions';
import BrandingShell from '../components/shared/BrandingShell';
import { cardSurfaceClass } from '../components/shared/cardSurface';
import axios from 'axios';
import { parseUtc } from '../lib/datetime';
import { logger } from '../lib/logger';


const API_BASE = window.location.origin;

const LOGO_SIZE_CLASSES = {
    small: 'max-h-16 max-w-[120px]',
    medium: 'max-h-20 max-w-[180px]',
    large: 'max-h-32 max-w-[280px]',
    very_large: 'max-h-44 max-w-[360px]'
};

const PLATFORM_COLORS = {
    google_reviews: { bg: 'rgba(66, 133, 244, 0.12)', border: 'rgba(66, 133, 244, 0.3)', hover: 'rgba(66, 133, 244, 0.2)' },
    facebook: { bg: 'rgba(24, 119, 242, 0.12)', border: 'rgba(24, 119, 242, 0.3)', hover: 'rgba(24, 119, 242, 0.2)' },
    instagram: { bg: 'rgba(225, 48, 108, 0.12)', border: 'rgba(225, 48, 108, 0.3)', hover: 'rgba(225, 48, 108, 0.2)' },
    trustpilot: { bg: 'rgba(0, 182, 122, 0.12)', border: 'rgba(0, 182, 122, 0.3)', hover: 'rgba(0, 182, 122, 0.2)' },
    yelp: { bg: 'rgba(211, 35, 35, 0.12)', border: 'rgba(211, 35, 35, 0.3)', hover: 'rgba(211, 35, 35, 0.2)' },
    tripadvisor: { bg: 'rgba(52, 224, 161, 0.12)', border: 'rgba(52, 224, 161, 0.3)', hover: 'rgba(52, 224, 161, 0.2)' }
};

const PLATFORM_SVGS = {
    google_reviews: (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
    ),
    facebook: (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#1877F2">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
    ),
    instagram: (
        <svg viewBox="0 0 24 24" className="w-7 h-7">
            <defs>
                <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FFDC80"/>
                    <stop offset="25%" stopColor="#F77737"/>
                    <stop offset="50%" stopColor="#E1306C"/>
                    <stop offset="75%" stopColor="#C13584"/>
                    <stop offset="100%" stopColor="#833AB4"/>
                </linearGradient>
            </defs>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" fill="url(#ig-grad)"/>
        </svg>
    ),
    trustpilot: (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#00B67A">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
        </svg>
    ),
    yelp: (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#D32323">
            <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.986-4.394c.564-.83 1.836-.35 1.836.693v3.204c0 .31-.238.593-.65.694zM13.244 15.99l1.433 4.995c.276.96-.8 1.74-1.63 1.176l-4.394-2.986c-.83-.564-.35-1.836.693-1.836h3.204c.31 0 .593.238.694.65zM10.756 8.01L9.323 3.015c-.276-.96.8-1.74 1.63-1.176l4.394 2.986c.83.564.35 1.836-.693 1.836H11.45c-.31 0-.593-.238-.694-.65zM3.84 11.406l4.995-1.433c.96-.276 1.74.8 1.176 1.63l-2.986 4.394c-.564.83-1.836.35-1.836-.693v-3.204c0-.31.238-.593.65-.694z"/>
        </svg>
    ),
    tripadvisor: (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#34E0A1">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
    )
};

const GlobalReview = () => {
    const { cardId } = useParams();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    // Guest tab title = the client's business name, never the portal's.
    useGuestTitle(data?.organization_name);
    const [screen, setScreen] = useState('language');
    const [enabledLangs, setEnabledLangs] = useState(['en', 'fr']);
    const [selecting, setSelecting] = useState(null);
    const [error, setError] = useState(null);
    const [vipSession, setVipSession] = useState(null);
    const [vipDelivered, setVipDelivered] = useState(null);

    useEffect(() => {
        loadData();
        // Check VIP session
        fetch(`${API_BASE}/api/vouchers/vip/check/${cardId}`)
            .then(r => r.ok ? r.json() : { active: false })
            .then(d => { if (d.active) setVipSession(d); })
            .catch(() => {});
    }, [cardId]);

    const deliverVip = async () => {
        if (!vipSession) return;
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/vip/deliver/${vipSession.session_id}`, { method: 'POST' });
            if (res.ok) {
                const d = await res.json();
                setVipDelivered(d);
                setVipSession(null);
            }
        } catch (err) {
            logger.warn('[GlobalReview] VIP delivery failed', err);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE}/api/global-review/${cardId}`);
            const d = response.data;

            if (d.fallback) {
                window.location.href = d.redirect_url;
                return;
            }

            setData(d);

            // Card-level language override (resolver: card override → org → default).
            // Legacy fallback: language_mode 'bilingual' => en+fr, 'en'/'fr' => single.
            const langs = (Array.isArray(d.enabled_languages) && d.enabled_languages.length > 0)
                ? d.enabled_languages
                : ((d.language_mode || 'bilingual') === 'bilingual' ? ['en', 'fr'] : [d.language_mode]);
            setEnabledLangs(langs);
            if (langs.length > 1) {
                setScreen('language');
            } else {
                i18n.changeLanguage(langs[0]);
                setScreen('destinations');
            }
        } catch (err) {
            console.error('Failed to load Global Review:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageSelect = (lang) => {
        i18n.changeLanguage(lang);
        setScreen('destinations');
    };

    const handleDestinationSelect = async (dest) => {
        setSelecting(dest.platform);
        try {
            const response = await axios.post(
                `${API_BASE}/api/global-review/${cardId}/select?platform=${dest.platform}`
            );
            window.location.href = response.data.redirect_url || dest.url;
        } catch {
            window.location.href = dest.url;
        }
    };

    // VIP Delivery Display
    if (vipDelivered) {
        return (
            <BrandingShell wallpaperUrl={vipDelivered?.background_image_url || null} variant="dark" data-testid="vip-customer-delivery">
                <div className="w-full max-w-xs space-y-6 text-center mx-auto">
                    <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto">
                        <Crown className="w-8 h-8 text-red-400" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-semibold text-white">{t('voucher.vipVoucher')}</h2>
                        <p className="text-4xl font-bold text-emerald-400">${vipDelivered.value}</p>
                        <p className="text-lg font-mono text-white tracking-wider mt-2">{vipDelivered.code}</p>
                    </div>
                    {vipDelivered.message && (
                        <p className="text-sm text-slate-400 italic">{vipDelivered.message}</p>
                    )}
                    <div className="bg-slate-900/50 backdrop-blur-sm rounded-lg p-3 text-left space-y-1 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>{t('voucher.validUntil')}</span>
                            <span className="text-slate-200">{parseUtc(vipDelivered.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        {vipDelivered.organization_name && (
                            <div className="flex justify-between text-slate-400">
                                <span>{t('voucher.from')}</span>
                                <span className="text-slate-200">{vipDelivered.organization_name}</span>
                            </div>
                        )}
                    </div>
                    <a href={`/voucher/${vipDelivered.code}`} className="block w-full">
                        <Button data-testid="vip-save-photo-customer" variant="outline" className="w-full border-slate-700 text-white hover:bg-slate-800">
                            {t('voucher.saveToPhotos')}
                        </Button>
                    </a>
                    <CardScanActions cardId={cardId} className="pt-2" />
                </div>
            </BrandingShell>
        );
    }

    if (loading) {
        return (
            <BrandingShell variant="dark">
                <div className="text-center mx-auto">
                    <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto" />
                    <p className="text-slate-300 mt-4">{t('globalReview.loading', 'Loading...')}</p>
                    <CardScanActions cardId={cardId} className="mt-6" />
                </div>
            </BrandingShell>
        );
    }

    if (error || !data) {
        return (
            <BrandingShell variant="danger">
                <div className="text-center max-w-md mx-auto">
                    <h1 className="text-2xl font-bold text-white mb-2" data-testid="global-review-error-title">
                        {t('globalReview.errorTitle', 'Something went wrong')}
                    </h1>
                    <p className="text-slate-300">{t('globalReview.error', 'Unable to load. Please try again.')}</p>
                    <CardScanActions cardId={cardId} className="mt-6" />
                </div>
            </BrandingShell>
        );
    }

    const logoUrl = data.logo_url;
    const logoSize = data.logo_size || 'medium';
    const logoClass = LOGO_SIZE_CLASSES[logoSize] || LOGO_SIZE_CLASSES.medium;
    // v3.5.17 — Wallpaper resolved from card payload; falls back to dark
    // gradient inside BrandingShell when null. Same surface across the flow.
    const wallpaperUrl = data?.background_image_url || null;
    // v4.34.3 — cardSurfaceClass is a function (not a string). Earlier
    // JSX referenced `${cardSurface}` directly which was undefined →
    // ReferenceError crashed the whole tree → white page on every Global
    // Review tap. Resolve it here once so both screens (language + destinations)
    // render the right frosted / clear / solid surface.
    const cardSurface = cardSurfaceClass(data?.card_style, 'dark');

    return (
        <BrandingShell
            wallpaperUrl={wallpaperUrl}
            variant="dark"
            data-testid="global-review-page"
        >
            <div className="w-full max-w-md text-center space-y-6 mx-auto">
                {/* Logo */}
                {logoUrl && (
                    <div className="flex justify-center animate-in fade-in duration-500">
                        <img src={logoUrl} alt="Logo" className={`${logoClass} object-contain rounded-lg`} data-testid="global-review-logo" />
                    </div>
                )}

                {/* Org Name */}
                {data.organization_name && (
                    <div className="animate-in fade-in duration-500" style={{ animationDelay: '100ms' }}>
                        <p className="text-emerald-400/70 text-xs uppercase tracking-widest">
                            {screen === 'language'
                                ? enabledLangs.map(l => ({ en: 'Welcome', fr: 'Bienvenue', es: 'Bienvenido' }[l])).filter(Boolean).join(' / ')
                                : t('globalReview.welcome', 'Welcome to')}
                        </p>
                        <h2 className="text-lg font-bold text-white">{data.organization_name}</h2>
                        {data.location_name && <p className="text-slate-400 text-sm">{data.location_name}</p>}
                    </div>
                )}

                {/* Language Selection — only the languages enabled for this card */}
                {screen === 'language' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="global-review-language-screen">
                        <div className={`${cardSurface} rounded-2xl p-6`}>
                            <h1 className="text-xl font-semibold text-white mb-6">
                                {enabledLangs.map(l => ({ en: 'Select your language', fr: 'Choisissez votre langue', es: 'Seleccione su idioma' }[l])).filter(Boolean).join(' / ')}
                            </h1>
                            <div className="space-y-3">
                                {enabledLangs.map(l => (
                                    <Button
                                        key={l}
                                        onClick={() => handleLanguageSelect(l)}
                                        size="lg"
                                        className="w-full py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg transition-all duration-300 hover:scale-[1.02]"
                                        data-testid={`global-review-lang-${l}`}
                                    >
                                        {{ en: 'English', fr: 'Français', es: 'Español' }[l]}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Destination Selection */}
                {screen === 'destinations' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="global-review-destinations-screen">
                        <div className={`${cardSurface} rounded-2xl p-6`}>
                            {/* Custom message or default heading */}
                            {(() => {
                                const lang = i18n.language || 'en';
                                const customMsg = lang === 'fr' ? data.custom_message_fr : data.custom_message_en;
                                if (customMsg) {
                                    return (
                                        <p className="text-white text-base leading-relaxed mb-6 whitespace-pre-line" data-testid="global-review-custom-message">
                                            {customMsg}
                                        </p>
                                    );
                                }
                                return (
                                    <>
                                        <h1 className="text-xl font-semibold text-white mb-2">
                                            {t('globalReview.chooseDestination', 'Where would you like to go?')}
                                        </h1>
                                        <p className="text-slate-400 text-sm mb-6">
                                            {t('globalReview.chooseDestinationSub', 'Select a platform to continue')}
                                        </p>
                                    </>
                                );
                            })()}

                            {data.destinations && data.destinations.length > 0 ? (
                                <div className="space-y-3">
                                    {data.destinations.map((dest, index) => {
                                        const colors = PLATFORM_COLORS[dest.platform] || PLATFORM_COLORS.google_reviews;
                                        const isSelecting = selecting === dest.platform;

                                        return (
                                            <button
                                                key={dest.platform}
                                                onClick={() => handleDestinationSelect(dest)}
                                                disabled={selecting !== null}
                                                className="w-full flex items-center gap-4 px-5 py-4 rounded-xl text-white transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 group"
                                                style={{
                                                    background: colors.bg,
                                                    border: `1px solid ${colors.border}`,
                                                    animationDelay: `${index * 80}ms`
                                                }}
                                                data-testid={`global-review-dest-${dest.platform}`}
                                            >
                                                <div className="flex-shrink-0">
                                                    {PLATFORM_SVGS[dest.platform] || (
                                                        <ExternalLink className="w-7 h-7 text-slate-400" />
                                                    )}
                                                </div>
                                                <span className="font-semibold text-base text-left flex-1">
                                                    {dest.label}
                                                </span>
                                                {isSelecting ? (
                                                    <Loader2 className="w-5 h-5 animate-spin text-white/60" />
                                                ) : (
                                                    <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-white/70 transition-colors" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-slate-500 text-sm" data-testid="global-review-no-destinations">
                                    {t('globalReview.noDestinations', 'No destinations configured.')}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Promo button — ADDED below the existing buttons of the step
                    the promo names, above the branding footer. Nothing is
                    rendered on any other step, or when no promo is live. */}
                <PromoButtons cardId={cardId} step={screen} lang={i18n.language} />

                {/* Branding */}
                {data.show_yulontap_branding !== false && (
                    <div className="pt-2 animate-in fade-in duration-500 flex flex-col items-center gap-2" style={{ animationDelay: '300ms' }}>
                        <CardScanActions cardId={cardId} />
                        {vipSession && (
                            <button
                                onClick={deliverVip}
                                data-testid="vip-customer-btn"
                                className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-8 py-2 rounded-full transition-colors shadow-lg shadow-red-600/30"
                            >
                                VIP
                            </button>
                        )}
                    </div>
                )}

            </div>
        </BrandingShell>
    );
};

export default GlobalReview;
